import { Dimensions, Platform, View, Pressable } from 'react-native';
import React, {
  useMemo,
  useEffect,
  useRef,
  useState,
  memo,
  useCallback,
  Suspense
} from "react";
import styles from "@stylesheet";
import Menu from "../Menu/Menu";
import MessageModal from "../../components/modals/ErrorModal/MessageModal";
import NotificationModal from '../../components/modals/NotificationModal/NotificationModal';
import { ToolbarContext } from '@hooks/toolbarContext';
import MailLoginModal from '../screens/MidLevel/MailScreen/Components/MailLoginModal';
import UpdateMessageModal from './Components/UpdateMessageModal';
import apis from '@api/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import HeaderBar from '@navigation/HeaderBar/HeaderBar';
import { useDispatch, useSelector } from 'react-redux';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import LogViewer from '@navigation/logViewer';
import Terminal from 'react-console-emulator';
import { Text } from '@constants/Themed';
import * as Animatable from 'react-native-animatable';
import { shallowEqual } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { TOGGLE_EASY_MODE, START_ONBOARDING } from '@reduxLocal/actions/types';
import { AlertDialogProvider } from '@components/Global/AlertDialog/AlertDialogProvider';
import { useNavigation, useRoute, useNavigationState } from "@react-navigation/native";
import ContextHandler from "@navigation/ContextHandler/ContextHandler";
import HelpSidePanel from '@components/modals/HelpModal/HelpSidePanel';
import PackageModal from '../../navigation/NavigationStacks/Components/PackageModal';
import EasyModeScreen from '@navigation/screens/MidLevel/EasyModeScreen/EasyModeScreen2';

// Lazy loaded components
const TutorialModal = React.lazy(() => import('@components/modals/TutorialModal/TutorialModalRefactored'));
import DashboardStack from './DashboardStack';
import PaymentRequiredBanner from './Components/PaymentRequiredBanner';
import MessagesSection from './Components/MessagesSection';
import FooterModals from './Components/FooterModals';
import CallModal from '@components/modals/CallModal/CallModal.web';
import { TrialExpirationModal } from '@components/modals/SaaSModals';
import { useTrialExpirationModal } from '@hooks/useTrialExpirationModal';
import FirstTutorial from '@components/modals/FirstTutorial/FirstTutorial.web';
import SocketManager from '@utils/socket';
import useGlobalUser from '@hooks/getGlobalUser';
import { NEW_NOTIFICATION } from '@reduxLocal/actions/types';
import { validateAndNotifyIntegrationConnections } from '@utils/validateIntegrationConnections';

// Memoized static components to prevent unnecessary re-renders
const MemoizedMenu = memo(Menu);
const MemoizedNotificationModal = memo(NotificationModal);
const MemoizedHeaderBar = memo(HeaderBar);
const MemoizedMessageModal = memo(MessageModal);
const MemoizedPackageModal = memo(PackageModal);
const MemoizedMailLoginModal = memo(MailLoginModal);
const MemoizedUpdateMessageModal = memo(UpdateMessageModal);

// Terminal commands configuration - extracted to prevent recreation on every render
const terminalCommands = {
  echo: {
    description: 'Echo a passed string',
    usage: 'echo <string>',
    fn: function () {
      return `${Array.from(arguments).join(' ')}`;
    }
  },
  help: {
    description: 'List available commands',
    usage: 'help',
    fn: function () {
      return Object.keys(this.commands).join(', ');
    }
  }
};

// CRITICAL: Create socket managers at module level to prevent recreation on component remounts
// This ensures they persist even if DashboardNavigator remounts
let globalNotificationsSocketManager: SocketManager | null = null;
let globalDataSocketManager: SocketManager | null = null;

const getNotificationsSocketManager = () => {
  if (!globalNotificationsSocketManager) {
    globalNotificationsSocketManager = new SocketManager('notifications');
  } else {
  }
  return globalNotificationsSocketManager;
};

const getDataSocketManager = () => {
  if (!globalDataSocketManager) {
    globalDataSocketManager = new SocketManager('data');
  } else {
  }
  return globalDataSocketManager;
};

const DashboardNavigator = ({ }: any) => {
  const dispatch = useDispatch();
  const theme = themeSelector();
  const logRef = useRef<any>(null);
  const packageModalRef = useRef<any>(null);

  // Use Redux selectors
  const logVisible = useSelector((state: DefaultRootState) => state.UI.advancedMode);
  const easyMode = useSelector((state: DefaultRootState) => state.UI.easyMode);
  const onboardingInProgress = useSelector((state: DefaultRootState) => state.UI.onboardingInProgress);
  const webScale = useSelector((state: DefaultRootState) => state.app?.webScale ?? 1);
  const learningMode = useSelector((state: DefaultRootState) => state.app.learningMode);
  const user = useSelector((state: DefaultRootState) => state.promiseData.data.userData, shallowEqual);
  const userTutorials = user?.tutorials;
  const isAuthenticated = useSelector((state: DefaultRootState) => state.promiseData.isAuthenticated);
  // Track navigation state to trigger cleanup on navigation changes
  const currentPage = useSelector((state: DefaultRootState) => state.navigation.page);

  // CRITICAL: Get actual navigation state from React Navigation to generate unique key
  // This ensures the key changes on every navigation.reset(), forcing unmount of old instances
  const navigationState = useNavigationState((state) => state);

  // CRITICAL: Generate a key for DashboardStack that changes on navigation reset
  // This forces React to unmount old instances and mount new ones, preventing memory leaks
  // The key must change when nested routes inside Dashboard change (navigation.reset() creates new nested routes)

  // Destructure billing information if available
  const { billingId, subscriptionId } = user?.billing ?? {};

  // Auto-enable Easy Mode for new users (only once, before onboarding starts)
  // Use a ref to track if we've already triggered onboarding to prevent re-triggering
  const onboardingTriggeredRef = useRef(false);

  useEffect(() => {
    // Handle learning mode users - they should always have easy mode enabled and onboarding triggered
    if (
      isAuthenticated &&
      user &&
      learningMode &&
      !onboardingInProgress &&
      !onboardingTriggeredRef.current
    ) {
      // Instrumentation only - no console logs
      onboardingTriggeredRef.current = true; // Mark as triggered - never reset to prevent re-triggering

      // Enable easy mode if not already enabled
      if (!easyMode) {
        dispatch({ type: TOGGLE_EASY_MODE });
      }

      // Start onboarding process
      dispatch({ type: START_ONBOARDING });
      return; // Exit early to prevent regular onboarding logic from running
    }

    // Regular onboarding logic for non-learning mode users
    // Only trigger if:
    // 1. User is authenticated
    // 2. User data is loaded
    // 3. Tutorial hasn't been completed (new user)
    // 4. Easy mode is currently off
    // 5. Onboarding is not in progress (to prevent re-triggering during onboarding)
    // 6. We haven't already triggered onboarding for this user
    // 7. Tutorial flag hasn't been set (meaning onboarding hasn't completed)
    // 8. User is not in learning mode (handled above)
    if (
      isAuthenticated &&
      user &&
      !userTutorials?.tutorialModal &&
      !easyMode &&
      !onboardingInProgress &&
      !onboardingTriggeredRef.current &&
      !learningMode
    ) {
      // Instrumentation only - no console logs
      onboardingTriggeredRef.current = true; // Mark as triggered - never reset to prevent re-triggering
      dispatch({ type: TOGGLE_EASY_MODE });
      dispatch({ type: START_ONBOARDING });
    }

    // If tutorial is completed, keep the ref set to true to prevent any re-triggering
    // This ensures that even if user data hasn't updated in Redux yet, we won't force easy mode again
    if (userTutorials?.tutorialModal) {
      onboardingTriggeredRef.current = true; // Keep it true to prevent re-triggering
    }
  }, [isAuthenticated, user, userTutorials?.tutorialModal, easyMode, onboardingInProgress, learningMode, dispatch]);

  // Ensure easy mode is always enabled for learning mode users
  useEffect(() => {
    if (isAuthenticated && user && learningMode && !easyMode) {
      // Instrumentation only - no console logs
      dispatch({ type: TOGGLE_EASY_MODE });
    }
  }, [isAuthenticated, user, learningMode, easyMode, dispatch]);

  // Centralized notification socket handling
  // This ensures a single socket connection and listener for all notification events
  const userId = useGlobalUser();

  // CRITICAL: Use module-level singleton socket managers to prevent recreation on remounts
  // This ensures they persist even if DashboardNavigator component remounts
  const notificationsSocketManager = getNotificationsSocketManager();
  const dataSocketManager = getDataSocketManager();

  // Track Redux state size and TanStack Query cache size to detect memory accumulation
  const reduxState = useSelector((state: DefaultRootState) => state);
  const queryClient = useQueryClient();



  const notificationsSocket = useMemo(() => {
    if (!userId) return null;
    const socket = notificationsSocketManager.getSocket(userId);
    return socket;
  }, [notificationsSocketManager, userId]);
  const dataSocket = useMemo(() => {
    if (!userId) return null;
    const socket = dataSocketManager.getSocket(userId);
    return socket;
  }, [dataSocketManager, userId]);

  // Use refs to track handlers so we can clean them up properly
  const handlersRef = useRef<{
    jobNotificationHandler?: (notification: any) => void;
    dataNotificationHandler?: (notification: any) => void;
    testEventHandler?: (data: any) => void;
    dataChangeHandler?: (change: any) => void;
    notificationsSocket?: any;
    dataSocket?: any;
  }>({});

  // Cleanup function that can be called from multiple places
  // CRITICAL: Use removeAllListeners() to ensure ALL listeners are removed, not just specific ones
  const cleanupSocketListeners = useCallback(() => {
    if (handlersRef.current.notificationsSocket) {
      const prevSocket = handlersRef.current.notificationsSocket;
      // Remove specific handlers if they exist
      if (handlersRef.current.jobNotificationHandler) {
        prevSocket.off('notification', handlersRef.current.jobNotificationHandler);
      }
      if (handlersRef.current.dataNotificationHandler) {
        prevSocket.off('data_notification', handlersRef.current.dataNotificationHandler);
      }
      if (handlersRef.current.testEventHandler) {
        prevSocket.off('test_event', handlersRef.current.testEventHandler);
      }
      // AGGRESSIVE: Remove ALL listeners for these events to catch any duplicates
      prevSocket.removeAllListeners('notification');
      prevSocket.removeAllListeners('data_notification');
      prevSocket.removeAllListeners('test_event');
    }
    if (handlersRef.current.dataSocket) {
      const prevSocket = handlersRef.current.dataSocket;
      // Remove specific handler if it exists
      if (handlersRef.current.dataChangeHandler) {
        prevSocket.off('data-change', handlersRef.current.dataChangeHandler);
      }
      // AGGRESSIVE: Remove ALL listeners for this event
      prevSocket.removeAllListeners('data-change');
    }
  }, []);

  // Track navigation changes more aggressively - use route key which changes on every navigation
  const navigationKey = useMemo(() => {
    return `${currentPage?.route?.name || ''}-${currentPage?.route?.key || ''}`;
  }, [currentPage?.route?.name, currentPage?.route?.key]);

  // Cleanup on navigation changes - this is the key fix!
  useEffect(() => {
    // Clean up previous listeners when navigation changes
    cleanupSocketListeners();
  }, [navigationKey, cleanupSocketListeners, currentPage?.route?.name]);

  useEffect(() => {
    // Instrumentation only - no console logs

    // CRITICAL: Clean up previous listeners BEFORE setting up new ones
    // This prevents accumulation when useEffect re-runs
    cleanupSocketListeners();

    // Store references for cleanup
    const currentNotificationsSocket = notificationsSocket;
    const currentDataSocket = dataSocket;

    if (!isAuthenticated || !userId) {
      // Instrumentation only - no console logs
      // Clear refs
      handlersRef.current = {};
      return;
    }

    if (!notificationsSocket || !dataSocket) {
      // Instrumentation only - no console logs
      // Clear refs
      handlersRef.current = {};
      return;
    }

    // Instrumentation only - no console logs

    // Ensure socket is connected
    if (!notificationsSocket?.connected) {
      // Instrumentation only - no console logs
      notificationsSocket?.connect();
    }

    const setupListeners = () => {
      if (!notificationsSocket) {
        // Instrumentation only - no console logs
        return;
      }

      // CRITICAL: Clean up ALL existing listeners BEFORE creating new handlers
      // This must happen BEFORE creating new handler functions
      // Get listener counts before cleanup - access UnifiedSocketManager through SocketManager
      const notificationsNamespace = (notificationsSocket as any)?._namespace || 'notifications';
      const dataNamespace = (dataSocket as any)?._namespace || 'data';
      // Get UnifiedSocketManager instance from the SocketManager (SocketManager stores it as unifiedManager)
      const unifiedManager = (notificationsSocketManager as any)?.unifiedManager || null;

      // Get current listener counts from UnifiedSocketManager
      let beforeNotificationCount = 0;
      let beforeDataCount = 0;
      if (unifiedManager && unifiedManager.eventListeners) {
        const nsListeners = unifiedManager.eventListeners.get(notificationsNamespace);
        if (nsListeners) {
          beforeNotificationCount = (nsListeners.get('notification')?.size || 0) +
            (nsListeners.get('data_notification')?.size || 0) +
            (nsListeners.get('test_event')?.size || 0);
        }
        const dataNsListeners = unifiedManager.eventListeners.get(dataNamespace);
        if (dataNsListeners) {
          beforeDataCount = dataNsListeners.get('data-change')?.size || 0;
        }
      }

      // Remove ALL listeners
      notificationsSocket.removeAllListeners('notification');
      notificationsSocket.removeAllListeners('data_notification');
      notificationsSocket.removeAllListeners('test_event');
      dataSocket.removeAllListeners('data-change');

      // Get listener counts after cleanup
      let afterCleanupNotificationCount = 0;
      let afterCleanupDataCount = 0;
      if (unifiedManager && unifiedManager.eventListeners) {
        const nsListeners = unifiedManager.eventListeners.get(notificationsNamespace);
        if (nsListeners) {
          afterCleanupNotificationCount = (nsListeners.get('notification')?.size || 0) +
            (nsListeners.get('data_notification')?.size || 0) +
            (nsListeners.get('test_event')?.size || 0);
        }
        const dataNsListeners = unifiedManager.eventListeners.get(dataNamespace);
        if (dataNsListeners) {
          afterCleanupDataCount = dataNsListeners.get('data-change')?.size || 0;
        }
      }


      // Instrumentation only - no console logs

      // Handler for job/notification events (from jobNotificationService)
      const jobNotificationHandler = async (notification: any) => {
        console.log('🔔 [DashStack] Job notification received:', {
          type: notification.type,
          title: notification.title,
          message: notification.message,
          jobName: notification.jobName,
          competitorId: notification.competitorId
        });
        try {
          const { mapJobNotification } = await import('@utils/notifications/notificationUtils');
          const mappedNotification = mapJobNotification(notification);
          console.log('🔔 [DashStack] Mapped notification:', mappedNotification);

          dispatch({
            type: NEW_NOTIFICATION,
            payload: mappedNotification
          });
          console.log('🔔 [DashStack] Dispatched NEW_NOTIFICATION action');

          // Invalidate queries for competitor discovery notifications
          if (notification.type === 'competitor_discovery_complete' || notification.type === 'competitor_discovery_failed') {
            console.log('🔔 [DashStack] Invalidating competitor queries for:', notification.competitorId);
            // Invalidate competitors list
            queryClient.invalidateQueries({ queryKey: ['competitors'] });
            // If we have competitorId, also invalidate that competitor's profiles
            if (notification.competitorId) {
              queryClient.invalidateQueries({ queryKey: ['competitor-profiles', notification.competitorId] });
            }
          }

          // Track notification in context tracker if applicable
          try {
            const { addContextEntry } = await import('@services/contextTracker');
            const notificationContent = JSON.stringify({
              type: notification.type,
              title: notification.title,
              message: notification.message,
              timestamp: notification.timestamp
            });
            await addContextEntry('notification', notificationContent, {
              type: notification.type,
              timestamp: notification.timestamp
            });
          } catch (error) {
            if (__DEV__) {
              // Instrumentation only - no console logs
            }
          }
        } catch (error) {
          // Instrumentation only - no console logs
        }
      };

      // Handler for data notifications (from notifications namespace - data_notification event)
      const dataNotificationHandler = async (notification: any) => {
        // Instrumentation only - no console logs
        try {
          const { mapDataNotification } = await import('@utils/notifications/notificationUtils');
          const mappedNotification = mapDataNotification(notification);
        // Instrumentation only - no console logs
          dispatch({
            type: NEW_NOTIFICATION,
            payload: mappedNotification
          });
          // Track notification in context tracker
          try {
            const { addContextEntry } = await import('@services/contextTracker');
            const notificationContent = JSON.stringify({
              type: notification.operationType,
              collection: notification.collection,
              message: notification.message,
              timestamp: notification.timestamp
            });
            await addContextEntry('notification', notificationContent, {
              operationType: notification.operationType,
              collection: notification.collection,
              timestamp: notification.timestamp
            });
          } catch (error) {
            if (__DEV__) {
          // Instrumentation only - no console logs
            }
          }
        } catch (error) {
          // Instrumentation only - no console logs
          // Fallback: dispatch original notification if mapping fails
          dispatch({
            type: NEW_NOTIFICATION,
            payload: notification
          });
        }
      };

      // Handler for data-change events (from data namespace - real-time data updates)
      const dataChangeHandler = async (change: any) => {
        // Instrumentation only - no console logs
        // Data-change events are for real-time updates, not necessarily notifications
        // But we can still dispatch them if needed
        if (change.message || change.operationType) {
          try {
            const { mapDataNotification } = await import('@utils/notifications/notificationUtils');
            const notificationData = {
              type: 'data_change',
              operationType: change.operationType,
              collection: change.collection,
              message: change.message || `${change.collection} was ${change.operationType}d`,
              timestamp: change.timestamp || new Date().toISOString(),
              ns: change.ns || (change.collection ? { coll: change.collection } : undefined),
              ...change
            };
            const mappedNotification = mapDataNotification(notificationData);
            dispatch({
              type: NEW_NOTIFICATION,
              payload: mappedNotification
            });
          } catch (error) {
          // Instrumentation only - no console logs
          // Fallback: dispatch original change if mapping fails
            dispatch({
              type: NEW_NOTIFICATION,
              payload: {
                type: 'data_change',
                operationType: change.operationType,
                collection: change.collection,
                message: change.message || `${change.collection} was ${change.operationType}d`,
                timestamp: change.timestamp || new Date().toISOString(),
                ...change
              }
            });
          }
        }
      };

      // Handler for test events
      const testEventHandler = async (data: any) => {
        // Instrumentation only - no console logs
        try {
          const { mapTestEvent } = await import('@utils/notifications/notificationUtils');
          const mappedNotification = mapTestEvent(data);
          // Instrumentation only - no console logs
          dispatch({
            type: NEW_NOTIFICATION,
            payload: mappedNotification
          });
        } catch (error) {
          // Instrumentation only - no console logs
        }
      };

      // Get listener counts BEFORE registering (after cleanup)
      let beforeRegisterNotificationCount = 0;
      let beforeRegisterDataCount = 0;
      if (unifiedManager && unifiedManager.eventListeners) {
        const nsListeners = unifiedManager.eventListeners.get(notificationsNamespace);
        if (nsListeners) {
          beforeRegisterNotificationCount = (nsListeners.get('notification')?.size || 0) +
            (nsListeners.get('data_notification')?.size || 0) +
            (nsListeners.get('test_event')?.size || 0);
        }
        const dataNsListeners = unifiedManager.eventListeners.get(dataNamespace);
        if (dataNsListeners) {
          beforeRegisterDataCount = dataNsListeners.get('data-change')?.size || 0;
        }
      }

      // Store handlers in ref for cleanup
      handlersRef.current = {
        jobNotificationHandler,
        dataNotificationHandler,
        testEventHandler,
        dataChangeHandler,
        notificationsSocket,
        dataSocket
      };

      // Register all listeners on notifications socket
      notificationsSocket.on('notification', jobNotificationHandler);
      notificationsSocket.on('data_notification', dataNotificationHandler);
      notificationsSocket.on('test_event', testEventHandler);
      // Instrumentation only - no console logs

      // Check listener count AFTER registering
      let afterRegisterNotificationCount = 0;
      if (unifiedManager && unifiedManager.eventListeners) {
        const nsListeners = unifiedManager.eventListeners.get(notificationsNamespace);
        if (nsListeners) {
          afterRegisterNotificationCount = (nsListeners.get('notification')?.size || 0) +
            (nsListeners.get('data_notification')?.size || 0) +
            (nsListeners.get('test_event')?.size || 0);
        }
      }

      // Register listeners on data socket
      dataSocket.on('data-change', dataChangeHandler);
      // Instrumentation only - no console logs

      // Check listener count AFTER registering
      let afterRegisterDataCount = 0;
      if (unifiedManager && unifiedManager.eventListeners) {
        const dataNsListeners = unifiedManager.eventListeners.get(dataNamespace);
        if (dataNsListeners) {
          afterRegisterDataCount = dataNsListeners.get('data-change')?.size || 0;
        }
      }

      // Return cleanup function (though cleanup happens at start of next effect run)
      return () => {
        // Instrumentation only - no console logs
        notificationsSocket.off('notification', jobNotificationHandler);
        notificationsSocket.off('data_notification', dataNotificationHandler);
        notificationsSocket.off('test_event', testEventHandler);
        dataSocket.off('data-change', dataChangeHandler);
      };
    };

    let cleanup: (() => void) | undefined;

    // Always ensure sockets are connected first
    if (!notificationsSocket.connected) {
      // Instrumentation only - no console logs
      notificationsSocket.connect();
    }
    if (!dataSocket.connected) {
      // Instrumentation only - no console logs
      dataSocket.connect();
    }

    // Set up listeners - either immediately if both connected, or wait for connect events
    const bothConnected = notificationsSocket.connected && dataSocket.connected;

    if (bothConnected) {
      // Instrumentation only - no console logs
      cleanup = setupListeners();
    } else {
      // Instrumentation only - no console logs

      let notificationsReady = notificationsSocket.connected;
      let dataReady = dataSocket.connected;

      const checkAndSetup = () => {
        if (notificationsReady && dataReady && !cleanup) {
          // Instrumentation only - no console logs
          cleanup = setupListeners();
        }
      };

      const notificationsConnectHandler = () => {
        // Instrumentation only - no console logs
        notificationsSocket.off('connect', notificationsConnectHandler);
        notificationsReady = true;
        checkAndSetup();
      };

      const dataConnectHandler = () => {
        // Instrumentation only - no console logs
        dataSocket.off('connect', dataConnectHandler);
        dataReady = true;
        checkAndSetup();
      };

      if (!notificationsReady) {
        notificationsSocket.once('connect', notificationsConnectHandler);
      }
      if (!dataReady) {
        dataSocket.once('connect', dataConnectHandler);
      }

      // Also set up a timeout to ensure we don't wait forever
      const timeoutId = setTimeout(() => {
        // Instrumentation only - no console logs
        if (!cleanup) {
          cleanup = setupListeners();
        }
      }, 5000);

      // Return cleanup function for useEffect
      return () => {
        clearTimeout(timeoutId);
        notificationsSocket.off('connect', notificationsConnectHandler);
        dataSocket.off('connect', dataConnectHandler);
        if (cleanup) {
          cleanup();
        }
      };
    }

    // Return cleanup function for useEffect - ALWAYS return cleanup
    return () => {
      // Instrumentation only - no console logs
      // Always cleanup listeners, even if setupListeners wasn't called
      if (cleanup) {
        cleanup();
      }
      // Final cleanup using stored references
      if (handlersRef.current.notificationsSocket) {
        const socket = handlersRef.current.notificationsSocket;
        if (handlersRef.current.jobNotificationHandler) {
          socket.off('notification', handlersRef.current.jobNotificationHandler);
        }
        if (handlersRef.current.dataNotificationHandler) {
          socket.off('data_notification', handlersRef.current.dataNotificationHandler);
        }
        if (handlersRef.current.testEventHandler) {
          socket.off('test_event', handlersRef.current.testEventHandler);
        }
      }
      if (handlersRef.current.dataSocket) {
        const socket = handlersRef.current.dataSocket;
        if (handlersRef.current.dataChangeHandler) {
          socket.off('data-change', handlersRef.current.dataChangeHandler);
        }
      }
      // Clear refs
      handlersRef.current = {};
    };
  }, [isAuthenticated, userId, notificationsSocket, dataSocket, dispatch]);

  // Query for subscription plan
  const { data: subscriptionData } = useQuery({
    queryKey: ['subscriptionPlan'],
    queryFn: async () => {
      const data = await apis.getSubscriptions(); // Remove parameters since function doesn't accept them
      return data;
    },
    refetchOnMount: true
  });

  // Query for integrations to validate connections on app load
  const { data: integrationData } = useQuery({
    queryKey: ['integrationData'],
    queryFn: () => apis.getIntegrations(),
    enabled: isAuthenticated && !!user, // Only fetch if user is authenticated
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  // Validate connected integrations on app load
  useEffect(() => {
    if (isAuthenticated && user && integrationData && Array.isArray(integrationData)) {
      const authentications = user?.authentications;

      if (authentications && Object.keys(authentications).length > 0) {
        // Run validation asynchronously to avoid blocking app initialization
        validateAndNotifyIntegrationConnections(integrationData, authentications).catch((error) => {
          // Silently handle errors to avoid disrupting the app
          console.error('Error validating integration connections on app load:', error);
        });
      }
    }
  }, [isAuthenticated, user, integrationData]);

  // Handlers
  const handleClearLogs = useCallback(() => logRef.current?.clear(), []);
  const handleRefreshLogs = useCallback(() => logRef.current?.refresh(), []);
  const route = useRoute();
  const navigation = useNavigation();

  // Redirect to login screen if user is not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      // Instrumentation only - no console logs
      (navigation as any).replace('Login');
    }
  }, [isAuthenticated, navigation]);

  const handleUpgrade = useCallback(() => {
    (navigation as any).navigate('Settings', { screen: 'Upgrade' });
  }, [navigation]);

  // Trial expiration modal logic
  const trialModal = useTrialExpirationModal();
  const handleTrialSubscribe = useCallback(() => {
    trialModal.onSubscribe();
    handleUpgrade();
  }, [trialModal, handleUpgrade]);
  // Toolbar state and context value
  const [refVal, setRefVal] = useState(null);
  const [optionsRef, setOptionsRef] = useState(null);
  const [reportRef, setReportRef] = useState(null);
  const [calendarContext, setCalendarContext] = useState(null);
  const [hierarchySocket, setHierarchySocket] = useState(null);
  const [projectContext, setProjectContext] = useState(null);
  const [calendarHeader, setCalendarHeader] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [editorContext, setEditorContext] = useState(null);

  // Memoize toolbar context to prevent unnecessary re-renders of all consumers
  const toolbar = useMemo(() => ({
    calendarHeader,
    setCalendarHeader,
    calendarContext,
    setCalendarContext,
    hierarchySocket,
    setHierarchySocket,
    draggedItem,
    setDraggedItem,
    ref: refVal,
    setRef: setRefVal,
    optionsRef,
    setOptionsRef,
    reportRef,
    setReportRef: () => setReportRef(null), // Wrapper to match interface signature
    projectContext,
    setProjectContext,
    editorContext,
    setEditorContext
  }), [
    calendarHeader, calendarContext, hierarchySocket, draggedItem,
    refVal, optionsRef, reportRef, projectContext, editorContext
  ]);

  // Memoize callback to prevent recreation on every render
  const handleUpdateCallback = useCallback(() => {
    // Instrumentation only - no console logs
  }, []);

  // Memoize log section for performance
  const logSection = useMemo(() => {
    return (
      logVisible && Platform.OS === 'web' && (
        <Animatable.View
          transition={"minWidth"}
          style={{
            minWidth: logVisible ? 450 : 0,
            flex: logVisible ? 0.1 : 0.000001
          }}
        >
          <View style={{ flex: 8, maxHeight: Platform.OS === 'web' ? '70vh' : Dimensions.get('window').height * 0.7, padding: 10 }}>
            <LogViewer ref={logRef} />
          </View>
          <View style={{ height: 40, flexDirection: "row" }}>
            <Pressable onPressIn={handleClearLogs} style={{ flex: 1, backgroundColor: 'black', justifyContent: 'center', alignItems: 'center' }}>
              <Text selectable={false} style={{ color: 'white' }}>Clear</Text>
            </Pressable>
            <Pressable onPress={handleRefreshLogs} style={{ flex: 1, backgroundColor: 'black', justifyContent: 'center', alignItems: 'center' }}>
              <Text selectable={false} style={{ color: 'white' }}>Refresh</Text>
            </Pressable>
          </View>
          <View style={{ flex: 2 }}>
            <Terminal
              commands={{
                ...terminalCommands,
                list: {
                  description: 'List all logs',
                  usage: 'list',
                  fn: async function () {
                    logRef.current?.refresh();
                    return `Log list refreshed`;
                  }
                }
              }}
              noDefaults
              welcomeMessage={'Connected to SmartSaaS terminal'}
              promptLabel={'> '}
              style={{
                flex: 1,
                height: '70%',
                width: '100%'
              }}
            />
          </View>
        </Animatable.View>
      )
    );
  }, [logVisible, handleClearLogs, handleRefreshLogs]);

  return (
    <AlertDialogProvider>
      <CallModal />
      <ToolbarContext.Provider value={toolbar}>
        <View style={{ flexDirection: "row", flexGrow: 1, maxHeight: Platform.OS === 'web' && webScale !== 1 ? `${100 / webScale}vh` : (Platform.OS === 'web' ? '100vh' : '100%') } as any}>
          {logSection}
          <View style={{ flex: 1, paddingRight: onboardingInProgress ? 0 : 70, flexDirection: "row", maxHeight: Platform.OS === 'web' && webScale !== 1 ? `${100 / webScale}vh` : (Platform.OS === 'web' ? '100vh' : Dimensions.get('window').height) } as any}>
            {!onboardingInProgress && <MemoizedMenu />}
            <View
              style={{
                zIndex: 1,
                flexDirection: 'column',
                marginLeft: -30,
                paddingLeft: 30,
                paddingTop: 10,
                flex: 10,
                overflow: 'hidden',
                width: '100%'
              }}
            >
              <MemoizedNotificationModal />
              <MemoizedHeaderBar />
              {/* Easy Mode Screen - Full screen overlay when enabled */}
              {/* Modals go here */}
              <MemoizedMessageModal />
              <MemoizedUpdateMessageModal hasUpdatePending={false} callback={handleUpdateCallback} />
              {/* <Suspense fallback={<Text>Loading Tutorial...</Text>}>
                <TutorialModal />
              </Suspense> */}
              {/* Only mount FirstTutorial after dashboard is loaded and onboarding is complete */}
              {!onboardingInProgress && <FirstTutorial />}
              <MemoizedPackageModal ref={packageModalRef} />
              <MemoizedMailLoginModal />
              <TrialExpirationModal
                open={trialModal.shouldShow}
                onClose={trialModal.onClose}
                onSubscribe={handleTrialSubscribe}
                daysRemaining={trialModal.daysRemaining || undefined}
                exceededLimits={trialModal.exceededLimits}
              />
              <View style={[styles.appContainer, { backgroundColor: "transparent" }]}>
                <PaymentRequiredBanner subscriptionData={subscriptionData} onUpgrade={handleUpgrade} />
              {easyMode && <EasyModeScreen />}
                <View style={{ backgroundColor: theme ? 'white' : 'black', flexDirection: 'row', flex: 1, alignItems: 'center', justifyContent: "center" }}>
                  <View
                    key='root-dash'
                    style={{
                      flex: 2,
                      paddingTop: 50,
                      justifyContent: 'center',
                      alignSelf: 'stretch',
                      alignContent: 'center',
                      flexGrow: 5,
                      position: 'relative',
                      maxWidth: 1280,
                    }}
                  >
                    {/* <Suspense fallback={
                      <View style={[styles.centerEverything, { justifyContent: 'center', maxHeight: "100%", flex: 1 }]}>
                        <Spinner />
                        <Paragraph style={{ color: theme ? "black" : "white" }}>Loading Dashboard...</Paragraph>
                      </View>
                    }> */}
                    <DashboardStack />
                    {/* </Suspense> */}
                  </View>
                </View>
              </View>
              <FooterModals />
            </View>
            <HelpSidePanel />
            <ContextHandler route={route} navigation={navigation} />
            {!onboardingInProgress && <MessagesSection />}
          </View>
        </View>
      </ToolbarContext.Provider>
    </AlertDialogProvider>
  )
};


// if (__DEV__) {
//   DashboardNavigator.whyDidYouRender = {
//   customName: "DashboardNavigator",
//   }
// }

export default memo(DashboardNavigator);
